import axios from 'axios';

// Always empty — dev proxy handles /api → localhost:8000,
// production FastAPI serves both API and built frontend from the same origin.
const api = axios.create({
  baseURL: '/api',
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ==================== AUTH ====================
export const authApi = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  updateUserRole: (userId, role) => api.put(`/auth/role/${userId}`, { role }),
  listUsers: () => api.get('/auth/users'),
};

// ==================== AGENTS ====================
export const agentsApi = {
  list: (projectId) => api.get('/agents/', { params: { project_id: projectId } }),
  get: (agentId) => api.get(`/agents/${agentId}`),
  create: (data) => api.post('/agents/', data),
  start: (agentId) => api.post(`/agents/${agentId}/start`),
  stop: (agentId) => api.post(`/agents/${agentId}/stop`),
  updateConfig: (agentId, config) => api.put(`/agents/${agentId}/config`, { config }),
  runTask: (agentId, input) => api.post(`/agents/${agentId}/run`, input),
};

// ==================== LEADS ====================
export const leadsApi = {
  list: (projectId, status) => api.get('/leads/', { params: { project_id: projectId, status } }),
  create: (data) => api.post('/leads/', data),
  generate: (projectId, criteria) => api.post('/leads/generate', { project_id: projectId, criteria }),
  update: (leadId, data) => api.put(`/leads/${leadId}`, data),
  delete: (leadId) => api.delete(`/leads/${leadId}`),
};

// ==================== CONTENT ====================
export const contentApi = {
  list: (projectId, contentType) => api.get('/content/', { params: { project_id: projectId, content_type: contentType } }),
  get: (contentId) => api.get(`/content/${contentId}`),
  generateImage: (projectId, prompt) => api.post('/content/image', { project_id: projectId, prompt }),
  generateVideo: (projectId, prompt, duration = 4) => api.post('/content/video', { project_id: projectId, prompt, duration }),
  generatePost: (projectId, topic, socialPlatform) => api.post('/content/post', { project_id: projectId, topic, social_platform: socialPlatform }),
  delete: (contentId) => api.delete(`/content/${contentId}`),
};

// ==================== PAYMENTS ====================
export const paymentsApi = {
  list: (projectId) => api.get('/payments/', { params: { project_id: projectId } }),
  stripeCheckout: (amount, originUrl) => api.post('/payments/stripe/checkout', { amount, origin_url: originUrl }),
  stripeStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
  flutterwaveInitiate: (data) => api.post('/payments/flutterwave/initiate', data),
  flutterwaveStatus: (txRef) => api.get(`/payments/flutterwave/status/${txRef}`),
  getStats: (projectId) => api.get('/payments/stats', { params: { project_id: projectId } }),
};

// ==================== USAGE ====================
export const usageApi = {
  get: (projectId, days = 7) => api.get('/usage/', { params: { project_id: projectId, days } }),
  getToday: (projectId) => api.get('/usage/today', { params: { project_id: projectId } }),
  getCaps: () => api.get('/usage/caps'),
  updateCaps: (caps) => api.put('/usage/caps', caps),
};

// ==================== SETTINGS ====================
export const settingsApi = {
  getConnectors: () => api.get('/settings/connectors'),
  updateConnector: (platform, data) => api.put(`/settings/connectors/${platform}`, data),
  testConnector: (platform) => api.post(`/settings/connectors/${platform}/test`),
};

// ==================== PROJECTS ====================
export const projectsApi = {
  list: () => api.get('/projects/'),
  get: (projectId) => api.get(`/projects/${projectId}`),
};

// ==================== OUTREACH QUEUE ====================
export const outreachQueueApi = {
  list: (tenantKey = 'pulse') => api.get('/outreach/queue', { params: { tenant_key: tenantKey } }),
  listFailed: (tenantKey = 'pulse') => api.get('/outreach/queue/failed', { params: { tenant_key: tenantKey } }),
  listHeld: (tenantKey = 'pulse') => api.get('/outreach/queue/held', { params: { tenant_key: tenantKey } }),
  releaseHold: (outreachId) => api.post(`/outreach/${outreachId}/release-hold`),
  approve: (outreachId) => api.post(`/outreach/${outreachId}/approve`),
  send: (outreachId) => api.post(`/outreach/${outreachId}/send`),
  sendAll: (tenantKey = 'pulse') => api.post('/outreach/send-all', null, { params: { tenant_key: tenantKey } }),
  reject: (outreachId) => api.post(`/outreach/${outreachId}/reject`),
  update: (outreachId, data) => api.patch(`/outreach/${outreachId}`, data),
};

export default api;
